import { ref } from '@nuxtjs/composition-api';
import { STEP_TYPE } from '~/constants/StepType';
import useSubscriptions from '~/composable/checkout/validations/useSubscriptions'
import { VALIDATION_STATUS } from '~/constants/ValidationStatus';
import useCheckoutInfo from '~/composable/useCheckoutInfo';
import useTlmCart from '~/composable/useTlmCart';
import useCustomer from '~/composable/customer/useCustomer';
import useLead from '~/composable/useLead';
import { CHECKOUT_FLOW, CUSTOMER_SUBSCRIPTION_TYPE } from '~/constants/CheckoutFlow';
import { runPrePaymentSignupValidate } from '~/composable/checkout/handler/orderValidateHelpers';

const appContext = ref(null)

function getPermissionInformationPayload (customerInfo) {
  return {
    permissionEmail: customerInfo.permissionsAccepted.value,
    permissionSms: customerInfo.permissionsAccepted.value,
    permissionCall: customerInfo.permissionsAccepted.value,
    permissionTermsText: customerInfo.permissionText1 + customerInfo.permissionText2 + customerInfo.linkText1
  }
}

async function finalizeSubscriptionTypeStep (state, appCtx) {
  const { customerInfo, numberInfo } = state
  const { addNumberInfo, addPermissionInformation } = useCheckoutInfo(appCtx)
  await addPermissionInformation(getPermissionInformationPayload(customerInfo))
  await addNumberInfo(numberInfo.value)
}

function isSimOnlySignUpMenu (menuType) {
  return menuType === CUSTOMER_SUBSCRIPTION_TYPE.NEW_NUMBER ||
    menuType === CUSTOMER_SUBSCRIPTION_TYPE.MOVE_NUMBER
}

async function finalizeCustomerInfoConfirmationStep (state, appCtx) {
  const customerInfo = state.customerInfo
  const { addLead } = useLead(appCtx)
  const { addAddressInformation } = useCustomer(appCtx)
  const { getCartID, isProtectedAddress, getItemAttributeFromCartItem, subscriptionList } = useTlmCart(appCtx)
  const { addPrepaidEmail } = useCheckoutInfo(appCtx)

  if (isProtectedAddress.value) {
    await addAddressInformation({
      useBillingAddressAsShipping: true,
      shippingAddress: customerInfo.customerDetails.value.addressDTO,
      billingAddress: customerInfo.customerDetails.value.addressDTO,
      emailAddress: customerInfo.customerDetails.value.email,
      cartID: getCartID.value
    })
  }
  await addPrepaidEmail(customerInfo.customerDetails.value.email)
  if (getItemAttributeFromCartItem(subscriptionList.value, 'PermissionEmail') === 'true') {
    await addLead()
  }
  const menuType = state.subscription.menu.value
  if (!isSimOnlySignUpMenu(menuType)) {
    return true
  }
  return runPrePaymentSignupValidate(state, appCtx, CHECKOUT_FLOW.WITH_MV_SUBSCRIPTION_ONLY)
}

export default function mobileVoiceSubscriptionOnlyFlowHandler () {
  const validValidationPromise = () => {
    return Promise.resolve({
      actionStatus: VALIDATION_STATUS.VALID,
      signupBlockingNotifications: [],
      hasSignupBlockingNotification: false
    });
  }
  const initialize = (app) => {
    return new Promise(() => {
      appContext.value = app;
    });
  }

  const preValidate = () => {
    const { preValidateSubscriptions } = useSubscriptions(appContext.value)
    return preValidateSubscriptions();
  }

  const validate = (subscriptionSignUpDataNew) => {
    const { validateSubscriptions } = useSubscriptions(appContext.value)
    return validateSubscriptions(subscriptionSignUpDataNew);
  }

  const execute = () => {
    return validValidationPromise()
  }

  const finalize = () => {
    return validValidationPromise()
  }

  const finalizeStep = async (stepType, state, appCtx) => {
    switch (stepType) {
      case STEP_TYPE.SUBSCRIPTION_TYPE:
        await finalizeSubscriptionTypeStep(state, appCtx)
        break
      case STEP_TYPE.CUSTOMER_INFO_CONFIRMATION: {
        const validated = await finalizeCustomerInfoConfirmationStep(state, appCtx)
        if (!validated) {
          return false
        }
        break
      }
      default:
        return true
    }
  }
  return {
    initialize,
    preValidate,
    validate,
    execute,
    finalize,
    finalizeStep
  }
}
